import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_BASE = "https://www.elegantaffairs.shop";
const DEFAULT_TITLE_SUFFIX = "Elegant Affairs — The Epitome of Luxury";
const DEFAULT_DESCRIPTION =
  "Hand-curated designer luxury bags from Saint Laurent, Versace, Gucci, Balenciaga, Prada and 40+ houses. Authenticated. Free shipping over $200. 14-day returns.";
// Text-based OG image — vector cdn endpoint that returns an SVG-style PNG.
// Hosted on og.emergent — picks up the title and renders a clean dark card.
const DEFAULT_OG_IMAGE = `${SITE_BASE}/og-image.png`;

/**
 * Site-wide SEO component. Drop into any page that needs custom meta.
 * Falls back to brand defaults when fields are omitted.
 */
export default function Seo({
  title,
  description,
  image,
  type = "website",
  noindex = false,
  jsonLd = null,
  canonicalPath,
}) {
  const location = useLocation();
  const path = canonicalPath ?? `${location.pathname}${location.search}`;
  const url = `${SITE_BASE}${path}`;

  const fullTitle = title
    ? `${title} | Elegant Affairs`
    : DEFAULT_TITLE_SUFFIX;
  const desc = description || DEFAULT_DESCRIPTION;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Elegant Affairs" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// ---------- helpers for callers ----------

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_BASE}/#organization`,
        name: "Elegant Affairs",
        description: "Hand-curated designer luxury bags. Authenticated, US-shipped.",
        url: SITE_BASE,
        logo: `${SITE_BASE}/og-image.png`,
        email: "concierge@elegantaffairs.shop",
        address: {
          "@type": "PostalAddress",
          streetAddress: "10405 N Centerway Dr Ste C #170",
          addressLocality: "Peoria",
          addressRegion: "IL",
          postalCode: "61615",
          addressCountry: "US",
        },
        slogan: "The Epitome of Luxury",
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_BASE}/#website`,
        url: SITE_BASE,
        name: "Elegant Affairs",
        description: "The Epitome of Luxury — designer luxury bags.",
        publisher: { "@id": `${SITE_BASE}/#organization` },
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE_BASE}/shop?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
    ],
  };
}

export function itemListJsonLd(products, listName = "Collection") {
  if (!Array.isArray(products) || products.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    numberOfItems: products.length,
    itemListElement: products.slice(0, 60).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_BASE}/product/${p.id}`,
      name: p.name,
    })),
  };
}

export function productJsonLd(p) {
  if (!p) return null;
  const img = p.images && p.images[0] ? p.images[0] : `${SITE_BASE}/og-image.png`;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description || `${p.brand} ${p.category.toLowerCase()} bag.`,
    image: img,
    sku: p.id,
    brand: { "@type": "Brand", name: p.brand },
    category: p.category,
    color: p.color || undefined,
    material: p.material || undefined,
    offers: {
      "@type": "Offer",
      url: `${SITE_BASE}/product/${p.id}`,
      priceCurrency: "USD",
      price: Number(p.price).toFixed(2),
      availability: (p.stock || 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "Elegant Affairs" },
    },
  };
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url ? `${SITE_BASE}${item.url}` : undefined,
    })),
  };
}
